import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { AddCircle, Refresh, CheckCircle, Devices, ContentCopy, Delete } from '@mui/icons-material';

import api from '../../../../../services/api';

// --- INTERFACES ---
interface Branch {
  id: number;
  nome: string;
  sigla?: string;
}

interface Company {
  id: number;
  nome: string;
  subtitulo: string;
  filiais: Branch[];
}

interface CollectorItem {
  id: number;
  collectorName: string;
  token: string;
  companyId: number;
  branchId: number;
  status: string | number;
  createdAtDate: string;
  createdAtTime: number;
  lastUsedDate?: string;
  lastUsedTime?: number;
}

export default function CollectorTokenManager() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [collectors, setCollectors] = useState<CollectorItem[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Form State
  const [collectorName, setCollectorName] = useState<string>('');
  const [companyId, setCompanyId] = useState<number>(1);
  const [branchId, setBranchId] = useState<number>(1);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  // Delete Dialog Target
  const [deleteTarget, setDeleteTarget] = useState<CollectorItem | null>(null);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ open: true, message, type });
  };

  // --- FETCH COMPANIES AND BRANCHES ---
  useEffect(() => {
    const fetchCompaniesAndBranches = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');

        if (data && Array.isArray(data) && data.length > 0) {
          setCompanyList(data);

          const firstCompany = data[0];
          setCompanyId(firstCompany.id);
          if (firstCompany.filiais && firstCompany.filiais.length > 0) {
            setBranchId(firstCompany.filiais[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
        showNotification('Erro ao carregar lista de empresas e filiais.', 'error');
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompaniesAndBranches();
    fetchCollectors();
  }, []);

  // --- FETCH COLLECTORS ---
  const fetchCollectors = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<CollectorItem[]>('app/data-collection/get-collectors-list');
      setCollectors(data || []);
    } catch (error) {
      console.error('Error fetching collectors:', error);
      showNotification('Erro ao carregar lista de coletores.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // --- HANDLE COMPANY CHANGE ---
  const handleCompanyChange = (e: SelectChangeEvent<number>) => {
    const selectedCompanyId = Number(e.target.value);
    setCompanyId(selectedCompanyId);

    const foundCompany = companyList.find(emp => emp.id === selectedCompanyId);
    if (foundCompany && foundCompany.filiais && foundCompany.filiais.length > 0) {
      setBranchId(foundCompany.filiais[0].id);
    }
  };

  const currentCompany = companyList.find(e => e.id === companyId) ||
    companyList[0] || {
      id: 1,
      nome: '',
      subtitulo: '',
      filiais: [],
    };

  // --- GENERATE TOKEN ---
  const handleGenerateToken = async () => {
    if (!collectorName.trim()) {
      showNotification('Informe o nome do coletor.', 'error');
      return;
    }

    try {
      setIsGenerating(true);
      const { data } = await api.post('app/data-collection/generate-collector-token', {
        collectorName,
        companyId,
        branchId,
      });

      setGeneratedToken(data.token);
      showNotification(`Token gerado para ${data.collectorName}!`, 'success');
      fetchCollectors();
    } catch (error: any) {
      console.error('Error generating token:', error);
      const msg = error?.response?.data?.message || 'Erro ao gerar token.';
      showNotification(msg, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  // --- DELETE COLLECTOR ---
  const handleDeleteCollector = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);
      const { data } = await api.delete(`app/data-collection/delete-collector/${deleteTarget.id}`);

      showNotification(data.message || 'Coletor excluído com sucesso!', 'success');
      setDeleteTarget(null);
      fetchCollectors();
    } catch (error: any) {
      console.error('Error deleting collector:', error);
      const msg = error?.response?.data?.message || 'Erro ao excluir o coletor.';
      showNotification(msg, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showNotification('Token copiado para a área de transferência!', 'info');
  };

  const formatMinutesToTime = (minutes?: number) => {
    if (minutes === undefined || minutes === null) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
        Gestão de Tokens de Coletores
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Gere tokens únicos para cada coletor de fábrica para liberar o acesso ao aplicativo.
      </Typography>

      {/* FORM GENERATOR */}
      <Paper
        elevation={2}
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" color="text.primary" mb={2}>
          Gerar Novo Token Único
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Nome do Coletor"
              placeholder="Ex: COLETOR-USINAGEM-01"
              value={collectorName}
              onChange={e => setCollectorName(e.target.value.toUpperCase())}
            />
          </Grid>

          {/* DYNAMIC COMPANY SELECT */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-company-label">Empresa</InputLabel>
              <Select
                labelId="select-company-label"
                value={companyId}
                label="Empresa"
                onChange={handleCompanyChange}
                disabled={isLoadingCompanies}
              >
                {companyList.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.nome} ({emp.subtitulo})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* DYNAMIC BRANCH SELECT */}
          <Grid item xs={12} sm={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-branch-label">Filial</InputLabel>
              <Select
                labelId="select-branch-label"
                value={branchId}
                label="Filial"
                onChange={e => setBranchId(Number(e.target.value))}
                disabled={isLoadingCompanies || !currentCompany.filiais.length}
              >
                {currentCompany.filiais?.map(fil => (
                  <MenuItem key={fil.id} value={fil.id}>
                    {fil.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleGenerateToken}
              disabled={isGenerating || isLoadingCompanies}
              startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AddCircle />}
              sx={{ fontWeight: 'bold' }}
            >
              Gerar Token Único
            </Button>
          </Grid>
        </Grid>

        {/* GENERATED TOKEN SUCCESS BOX */}
        {generatedToken && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 2.5,
              bgcolor: isDark ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.success.light, 0.2),
              border: `2px solid ${theme.palette.success.main}`,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="success.main" fontWeight="bold" display="block">
                TOKEN GERADO E PRONTO PARA CADASTRO NO COLETOR:
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                {generatedToken}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              onClick={() => copyToClipboard(generatedToken)}
              startIcon={<ContentCopy />}
              sx={{ fontWeight: 'bold' }}
            >
              Copiar Token
            </Button>
          </Paper>
        )}
      </Paper>

      {/* COLLECTORS TABLE */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
            Coletores Cadastrados
          </Typography>
          <IconButton onClick={fetchCollectors} disabled={isLoading}>
            <Refresh />
          </IconButton>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  bgcolor: isDark ? 'grey.900' : 'grey.100',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              <TableCell>Coletor</TableCell>
              <TableCell>Empresa / Filial</TableCell>
              <TableCell>Token de Conexão Único</TableCell>
              <TableCell align="center">Situação</TableCell>
              <TableCell>Último Acesso</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collectors.map(row => (
              <TableRow key={row.id} hover sx={{ '& td': { borderColor: theme.palette.divider } }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Devices fontSize="small" color="action" />
                    {row.collectorName}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  Emp: {row.companyId} | Fil: {row.branchId}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'text.primary' }}>
                  {row.token}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<CheckCircle />}
                    label={row.status === 'A' || row.status === 1 ? 'Ativo' : 'Inativo'}
                    color={row.status === 'A' || row.status === 1 ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {row.lastUsedDate
                    ? `${row.lastUsedDate.split('T')[0]} ${formatMinutesToTime(row.lastUsedTime)}`
                    : 'Nunca acessou'}
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Tooltip title="Copiar Token">
                      <IconButton size="small" onClick={() => copyToClipboard(row.token)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir Coletor">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText color="text.secondary">
            Deseja realmente excluir o coletor <b>{deleteTarget?.collectorName}</b>?
            <br />
            Esta ação invalidará o token <b>{deleteTarget?.token}</b> e impedirá o envio de apontamentos por este
            terminal.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit" disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteCollector}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <Delete />}
            sx={{ fontWeight: 'bold' }}
          >
            Excluir Coletor
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={notification.type} variant="filled" sx={{ fontWeight: 'bold' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
